import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MENU, MY_APPS_MENU_ITEM } from './menu';
import { MenuItem } from './menu.model';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { SidebarStateService } from 'src/app/core/services/sidebar-state.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { PatientSearchModalComponent } from 'src/app/pages/patient-search-modal/patient-search-modal.component';
import { SignatureComponent } from 'src/app/pages/signature/signature.component';
import { DelegationsModalComponent } from 'src/app/pages/delegations-modal/delegations-modal.component';
import { RecordingNavigationGuardService } from 'src/app/core/services/recording-navigation-guard.service';
import { AdmissionsService } from 'src/app/core/services/admissions.service';
import { FilterService } from 'src/app/core/services/filter.service';
import { ScribeListStateService } from 'src/app/core/services/scribe-list-state.service';
import { clearDashboardWorklistPatientIdsFilter } from 'src/app/core/utils/dashboard-worklist-patient-ids.util';
import { WorksheetAppliedFiltersLocalStorageService } from 'src/app/pages/worksheet/worksheet-applied-filters-local-storage.service';
import { Subject, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { AvailableApplication } from 'src/app/core/models/environment.interface';
import { CHARTS_V3_LICENSE_NAME, canAccessBillerApp } from 'src/app/core/utils/organization-services-license.util';
import { getWorkplaceUserCompanies } from 'src/app/core/utils/account-organization.util';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})

export class SidebarComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];
  readonly myAppsMenuItem = MY_APPS_MENU_ITEM;
  expandSidebar: boolean = false;
  currentUserName: string = '';
  currentUserEmail: string = '';
  currentUserInitials: string = '';
  currentCompanyName: string = '';
  isSidebarActive: boolean = false;
  showUserPopup: boolean = false;
  hoveredItem: MenuItem | null = null;
  sidebarTooltipLabel = '';
  sidebarTooltipTop = 0;
  sidebarTooltipVisible = false;
  isUserScheduleResource: boolean = false;
  private destroy$ = new Subject<void>();
  availableLicenses: string[];
  canAccessBiller = false;
  showCompanySwitch = false;
  constructor(public readonly _router: Router,
    private readonly _modalService: BsModalService,
    private readonly _authService: AuthenticationService,
    private readonly _recordingGuard: RecordingNavigationGuardService,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _sidebarStateService: SidebarStateService,
    private readonly _admissionsService: AdmissionsService,
    private readonly _scribeListStateService: ScribeListStateService,
    private readonly _filterService: FilterService,
    private readonly _worksheetAppliedFiltersLocalStorage: WorksheetAppliedFiltersLocalStorageService
  ) {
    this.menuItems = MENU;
  }

  ngOnInit(): void {
    // Ensure sidebar is always collapsed (false) on initialization
    // It should only expand when user manually toggles it
    this.expandSidebar = false;
    this._sidebarStateService.setSidebarExpanded(false);

    // Subscribe to application context to get current user
    this._applicationContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe(context => {
        this.availableLicenses = context.companyLicenses;
        this.canAccessBiller = canAccessBillerApp(
          context.companyLicenses,
          context.currentlyLoggedInUser
        );
        if (context?.currentlyLoggedInUser) {
          const user = context.currentlyLoggedInUser;
          this.currentUserName = `${user.firstName} ${user.lastName}`.trim();
          this.currentUserEmail = user.email || '';
          
          // Generate user initials
          const firstInitial = user.firstName?.charAt(0)?.toUpperCase() || '';
          const lastInitial = user.lastName?.charAt(0)?.toUpperCase() || '';
          this.currentUserInitials = `${firstInitial}${lastInitial}`;
          
          // Get company name directly from user object (comes from API)
          this.currentCompanyName = user.companyName || '';

          this.showCompanySwitch =
            getWorkplaceUserCompanies(context.userCompanies).length > 1;
          
          // Check if user is a schedule resource
          this.isUserScheduleResource = user.isScheduleResource || false;
        }
      });

    // Subscribe to sidebar active state changes
    this._sidebarStateService.sidebarActive$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => {
        this.isSidebarActive = isActive;
      });

    // Track route changes to set sidebar active state
    this._router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateSidebarActiveState();
      });

    // Set initial sidebar active state
    this.updateSidebarActiveState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInternalMenuClick(event: MouseEvent, menuItem: MenuItem): void {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
      return;
    }
    event.preventDefault();
    this.navigateToMenuItem(menuItem);
  }

  onMenuContextMenu(event: MouseEvent, menuItem: MenuItem): void {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    if (!this.canOpenMenuItem(menuItem)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.hideSidebarTooltip();
    this.openMenuItemInNewTab(menuItem);
  }

  private canOpenMenuItem(menuItem: MenuItem): boolean {
    if (menuItem.appLicenseKey) {
      return !!this.availableLicenses?.includes(menuItem.appLicenseKey);
    }
    if (menuItem.hasExternalLink && menuItem.label?.toLowerCase().includes('engage')) {
      return !!this.availableLicenses?.includes('engage');
    }
    return true;
  }

  private openMenuItemInNewTab(menuItem: MenuItem): void {
    if (menuItem.hasExternalLink) {
      if (menuItem.appLicenseKey) {
        this.appClicked(menuItem.appLicenseKey, true);
        return;
      }
      this.menuItemClicked(menuItem);
      return;
    }
    window.open(new URL(menuItem.link, window.location.origin).href, '_blank', 'noopener,noreferrer');
  }

  navigateToMenuItem(menuItem: MenuItem) {
    // Ensure sidebar stays collapsed when navigating
    // It should only expand when user manually toggles it
    this.expandSidebar = false;
    this._sidebarStateService.setSidebarExpanded(false);

    // Check if link contains query parameters
    const [path, queryString] = menuItem.link.split('?');

    // Entering a persisted listing through the main navigation starts with fresh filters.
    // Detail-page/back navigation does not pass through here, so its state remains preserved.
    if (
      path === "/referrals" ||
      path === "/admissions" ||
      path === "/procedure-list" ||
      path === "/scribe" ||
      path === "/engage"
    ) {
      this.resetPersistedFiltersForLeftNav(path);

      const currentPath = this._router.url.split("?")[0];
      const alreadyOnRoute = currentPath === path;
      if (alreadyOnRoute) {
        this._router.navigate([path], { queryParams: { refresh: "1" } });
      } else {
        this._router.navigate([path]);
      }
      return;
    }

    // Parse query parameters if they exist
    const queryParams: any = {};
    if (queryString) {
      queryString.split('&').forEach(param => {
        const [key, value] = param.split('=');
        queryParams[key] = value;
      });
    }

    // Navigate with query parameters
    if (Object.keys(queryParams).length > 0) {
      this._router.navigate([path], { queryParams });
    } else {
      this._router.navigate([path]);
    }
  }

  /** Clear page-owned persisted filters only when opened from left navigation. */
  private resetPersistedFiltersForLeftNav(path: string): void {
    if (path === "/scribe") {
      this._scribeListStateService.clear();
      this._admissionsService.clearScribeCache();
      return;
    }

    if (path === "/engage") {
      clearDashboardWorklistPatientIdsFilter(this._filterService);
      this._filterService.clearAll();
      this._filterService.clearDosFilters();
      this._admissionsService.clearWorklistToolbarEngageSelections();
      this._admissionsService.resetReferralsFilters();
      this._admissionsService.clearWorklistPageResultCache();
      this._worksheetAppliedFiltersLocalStorage.clear();
      return;
    }

    this._admissionsService.resetReferralsFilters();
    if (path === "/procedure-list") {
      this._admissionsService.clearProcedureListSheetFilters();
      this._admissionsService.procedureListActiveTabId = "today";
    }
  }

  menuItemClicked(menuItem: MenuItem) {
    // Ensure sidebar stays collapsed when clicking external links
    this.expandSidebar = false;
    this._sidebarStateService.setSidebarExpanded(false);
    
    if (!menuItem.hasExternalLink) {
      return;
    }
    this._recordingGuard.checkAndBlockNavigation(() => {
      const linkWithToken = this.buildCrossAppHandoffUrl(menuItem.link);
      window.open(linkWithToken, '_blank');
    });
  }

  openSettingsApp() {
    this._recordingGuard.checkAndBlockNavigation(() => {
      const linkWithToken = this.buildCrossAppHandoffUrl(environment.adminAppUrl);
      window.open(linkWithToken, '_blank');
    });
  }

  isCurrentRoute(route: string): boolean {
    // Extract path without query params from the route
    const routePath = route.split('?')[0];
    const currentPath = this._router.url.split('?')[0];
    if (currentPath === routePath) {
      return true;
    }
    // Child routes (e.g. /engage/prospect) should keep Engage active + gradient icon
    if (routePath === '/engage' && currentPath.startsWith('/engage/')) {
      return true;
    }
    if (
      routePath === '/procedure-list' &&
      currentPath.startsWith('/procedures/')
    ) {
      return true;
    }
    return false;
  }

  isChatRouteActive(): boolean {
    return this._router.url === '/' || this._router.url.startsWith('/?') || this._router.url === '/chats' || this._router.url.startsWith('/chats?');
  }

  updateSidebarActiveState(): void {
    const currentUrl = this._router.url;
    const currentPath = currentUrl.split('?')[0];
    let isActive = false;

    // Check if current route matches any menu item
    const navItems = [...this.menuItems, this.myAppsMenuItem];
    for (const item of navItems) {
      const itemPath = item.link.split('?')[0];
      if (itemPath === currentPath || 
          (itemPath === '/' && this.isChatRouteActive()) ||
          (itemPath !== '/' && currentPath.startsWith(itemPath))) {
        isActive = true;
        break;
      }
    }

    this._sidebarStateService.setSidebarActive(isActive);
  }

  openSeachModal() {
    this._recordingGuard.checkAndBlockNavigation(() => {
      const options: ModalOptions = {
        class: 'patient-search-modal modal-lg',
        ignoreBackdropClick: true
      };
      this._modalService.show(PatientSearchModalComponent, options);
    });
  }

  openSignatureModal(): void {
    this.closeUserPopup();
    const options: ModalOptions = {
      class: 'signature-modal modal-lg modal-dialog-centered',
      ignoreBackdropClick: true,
      backdrop: 'static'
    };
    this._modalService.show(SignatureComponent, options);
  }

  openDelegationsModal(): void {
    this.closeUserPopup();
    const options: ModalOptions = {
      class: 'signature-modal modal-lg modal-dialog-centered',
      ignoreBackdropClick: true,
      backdrop: 'static'
    };
    this._modalService.show(DelegationsModalComponent, options);
  }

  gotoSelectCompanyPage(): void {
    this.closeUserPopup();
    this._recordingGuard.checkAndBlockNavigation(() => {
      void this._router.navigateByUrl('/select-company?reason=switch');
    });
  }

  toggleSidebar(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.expandSidebar = !this.expandSidebar;
    this._sidebarStateService.setSidebarExpanded(this.expandSidebar);
    this.hideSidebarTooltip();
  }

  collapseSidebarFromLogo(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.expandSidebar) {
      return;
    }
    this.expandSidebar = false;
    this._sidebarStateService.setSidebarExpanded(false);
  }

  onConversationSelected(conversationId: string) {
    // Ensure sidebar stays collapsed when selecting a conversation
    this.expandSidebar = false;
    this._sidebarStateService.setSidebarExpanded(false);
    
    // Navigate to chats page and pass the conversation ID
    if (conversationId) {
      this._router.navigate(['/chats'], { queryParams: { conversationId } });
    } else {
      this._router.navigate(['/chats']);
    }
  }

  toggleUserPopup(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.showUserPopup = !this.showUserPopup;
  }

  closeUserPopup() {
    this.showUserPopup = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // This will be called for any click in the document
    // The popup itself stops propagation, so clicks inside won't reach here
    if (this.showUserPopup) {
      this.closeUserPopup();
    }
  }

  hasEngageLicense(): boolean {
    return this.availableLicenses && this.availableLicenses.length > 0;
  }

  appClicked(app: string, hasLicense: boolean) {
    // Ensure sidebar stays collapsed when clicking apps
    this.expandSidebar = false;
    this._sidebarStateService.setSidebarExpanded(false);
    
    if (!hasLicense) {
      return;
    }
    switch (app) {
      case AvailableApplication.Charts:
        this.openUrl(environment.chartsAppUrl);
        return;
      case AvailableApplication.Remit:
        this.openUrl(environment.remitAppUrl)
        return;
      case AvailableApplication.Admin:
        this.openUrl(environment.adminAppUrl)
        return;
      case AvailableApplication.Biller:
        this.openUrl(environment.billerAppUrl)
        return;
      case AvailableApplication.Studies:
        this.openUrl(environment.studiesAppUrl)
        return;
      case AvailableApplication.Engage:
        this.openUrl(environment.engageAppUrl)
        return;
      case AvailableApplication.Phi:
        this.openUrl(environment.phiAppUrl)
        return;
      case AvailableApplication.Vascunote:
        window.open('https://vapps.vmamd.com/', '_blank');
    }
  }
  openUrl(url: string): void {
    const linkWithToken = this.buildCrossAppHandoffUrl(url);
    window.open(linkWithToken, '_blank');
  }

  private buildCrossAppHandoffUrl(url: string, patientId?: string): string {
    const ctx = this._applicationContextService.contextSnapshot;
    return this._authService.appendEncryptedAuthInfo(url, patientId, {
      userId: ctx?.currentlyLoggedInUser?.id,
      companyId: ctx?.currentlyLoggedInUser?.companyId,
    });
  }
  logout() {
    this.closeUserPopup();
    this._recordingGuard.checkAndBlockNavigation(() => {
      // Show SweetAlert2 confirmation dialog
      Swal.fire({
        title: 'Are you sure you want to logout?',
        text: 'You will be logged out of all applications',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#513dca',
        cancelButtonColor: '#9ca3af',
        confirmButtonText: 'Logout',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          this._authService.logoutFromAllApps();
        }
        // If user clicked "Cancel", do nothing (just close the popup)
      });
    });
  }

  onMenuItemHover(item: MenuItem, event?: Event): void {
    this.hoveredItem = item;
    if (event) {
      this.showSidebarTooltip(item.label, event);
    }
  }

  onMenuItemLeave(): void {
    this.hoveredItem = null;
    this.hideSidebarTooltip();
  }

  showSidebarTooltip(label: string, event: Event): void {
    if (this.expandSidebar) {
      this.hideSidebarTooltip();
      return;
    }
    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      return;
    }
    const rect = target.getBoundingClientRect();
    this.sidebarTooltipLabel = label;
    this.sidebarTooltipTop = rect.top + rect.height / 2;
    this.sidebarTooltipVisible = true;
  }

  hideSidebarTooltip(): void {
    this.sidebarTooltipVisible = false;
    this.sidebarTooltipLabel = '';
  }

  isItemHovered(item: MenuItem): boolean {
    return this.hoveredItem === item;
  }

  shouldShowMenuItem(item: MenuItem): boolean {
    // Engage product license: Patients page (formerly /engage)
    if (item.link?.split('?')[0] === '/patients') {
      return this.availableLicenses?.includes('engage');
    }

    // charts-v3 license from company_licenses: procedure list and related flows
    if (item.link?.split('?')[0] === '/procedure-list') {
      return this.availableLicenses?.includes(CHARTS_V3_LICENSE_NAME) ?? false;
    }

    if (item.link?.split('?')[0] === '/biller') {
      return this.canAccessBiller;
    }

    // Licensed external apps — show entry; disabled state handled in template
    if (item.appLicenseKey) {
      return true;
    }
    
    // Show all other menu items
    return true;
  }
}